import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Users, Merge, ChevronRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function B2CDuplicates() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeCtx, setMergeCtx] = useState(null); // { primary, others: [...] }
  const nav = useNavigate();
  const load = async () => {
    setLoading(true);
    const r = await api.get("/b2c/duplicate-students"); setGroups(r.data.groups || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openMerge = (group) => {
    const sorted = [...group.students].sort((a,b) => (b.application_count+b.semester_count+b.commission_count) - (a.application_count+a.semester_count+a.commission_count));
    setMergeCtx({ group, primaryId: sorted[0].id, selected: Object.fromEntries(sorted.slice(1).map(s => [s.id, true])) });
  };

  const runMerge = async () => {
    const dup_ids = Object.entries(mergeCtx.selected).filter(([_,v])=>v).map(([k])=>k);
    if (dup_ids.length === 0) return toast.error("Select at least one duplicate");
    try {
      const r = await api.post("/b2c/merge-students", { primary_id: mergeCtx.primaryId, duplicate_ids: dup_ids });
      toast.success(`Merged ${r.data.stats.students_merged} into ${r.data.primary_student_id} — moved ${r.data.stats.applications} apps, ${r.data.stats.semesters} semesters, ${r.data.stats.commissions} commissions, ${r.data.stats.files} docs`);
      setMergeCtx(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Duplicate Students</h1>
        <p className="text-sm text-slate-500 mt-1">Groups of students who share a UCI, email, or phone. Merge them into one master record — nothing is deleted, history is preserved.</p>
      </div>

      {loading && <div className="bg-white p-8 rounded-xl border border-slate-200 text-slate-500 text-sm">Scanning for duplicates…</div>}
      {!loading && groups.length === 0 && (
        <div className="bg-white p-8 rounded-xl border border-slate-200 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mb-3"><ShieldAlert className="w-6 h-6"/></div>
          <div className="heading-font text-lg text-slate-900">No duplicates found</div>
          <p className="text-sm text-slate-500 mt-1">Great — your student list is clean.</p>
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-amber-200 overflow-hidden" data-testid={`dup-group-${idx}`}>
            <div className="px-4 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-900">
                <Users className="w-4 h-4" />
                <span className="font-medium text-sm">Matching {g.match_field}: <span className="mono">{g.match_value}</span></span>
                <span className="stage-badge bg-white text-amber-900 border-amber-300">{g.count} records</span>
              </div>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={()=>openMerge(g)} data-testid={`merge-open-${idx}`}>
                <Merge className="w-4 h-4 mr-2"/>Merge
              </Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-4 py-2">Student ID</th><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Phone</th><th className="text-left px-4 py-2">UCI</th><th className="text-right px-4 py-2">Apps</th><th className="text-right px-4 py-2">Sems</th><th className="text-right px-4 py-2">Claims</th><th></th></tr></thead>
              <tbody>
                {g.students.map(s => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={()=>nav(`/b2c/students/${s.id}`)}>
                    <td className="px-4 py-2 mono text-xs">{s.student_id}</td>
                    <td className="px-4 py-2 font-medium">{s.full_name}</td>
                    <td className="px-4 py-2">{s.email||"—"}</td>
                    <td className="px-4 py-2">{s.phone||"—"}</td>
                    <td className="px-4 py-2">{s.uci||"—"}</td>
                    <td className="px-4 py-2 text-right">{s.application_count}</td>
                    <td className="px-4 py-2 text-right">{s.semester_count}</td>
                    <td className="px-4 py-2 text-right">{s.commission_count}</td>
                    <td className="px-4 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Dialog open={!!mergeCtx} onOpenChange={v=>!v && setMergeCtx(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>Merge Students</DialogTitle></DialogHeader>
          {mergeCtx && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Pick the <b>master record</b> to keep. All applications, semesters, commission claims and documents from the others will be transferred to it. Missing fields on the master (email/phone/UCI/etc.) will be filled from the merged records.</p>
              <div className="space-y-2">
                {mergeCtx.group.students.map(s => {
                  const isPrimary = mergeCtx.primaryId === s.id;
                  return (
                    <label key={s.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${isPrimary ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white"}`}>
                      <input type="radio" name="primary" data-testid={`primary-${s.student_id}`} checked={isPrimary}
                             onChange={()=>{ const sel = {...mergeCtx.selected}; delete sel[s.id]; mergeCtx.group.students.forEach(x => { if (x.id !== s.id) sel[x.id] = sel[x.id] !== false; }); setMergeCtx({...mergeCtx, primaryId: s.id, selected: sel}); }} />
                      <div className="flex-1">
                        <div className="font-medium">{s.full_name} <span className="mono text-xs text-slate-500 ml-2">{s.student_id}</span></div>
                        <div className="text-xs text-slate-500">{[s.email, s.phone, s.uci && `UCI ${s.uci}`].filter(Boolean).join(" · ") || "—"}</div>
                        <div className="text-xs text-slate-500">{s.application_count} apps · {s.semester_count} semesters · {s.commission_count} commissions</div>
                      </div>
                      {isPrimary ? (
                        <span className="stage-badge bg-orange-500 text-white border-orange-500">MASTER</span>
                      ) : (
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={mergeCtx.selected[s.id] !== false}
                                 data-testid={`merge-select-${s.student_id}`}
                                 onChange={e=>setMergeCtx({...mergeCtx, selected: {...mergeCtx.selected, [s.id]: e.target.checked}})} />
                          merge in
                        </label>
                      )}
                    </label>
                  );
                })}
              </div>
              <div className="text-xs text-amber-800 bg-amber-50 p-3 rounded">Merged records are marked (never deleted). An audit entry is created on the master. This action cannot be undone from the UI.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setMergeCtx(null)}>Cancel</Button>
            <Button data-testid="merge-confirm" onClick={runMerge} className="bg-orange-500 hover:bg-orange-600 text-white"><Merge className="w-4 h-4 mr-2"/>Merge Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
